/**
 * Map Agent BQ surveillance graph envelopes → feature types.
 * Lab spellings (Surveilliance) kept on wire paths only.
 */

import type { SurveillanceBet, SurveillanceGraph } from '@/features/surveillance/types'

/** Static fallbacks when Agent omits `message` or the catch has no Error. */
export const SURVEILLANCE_STATIC_ERRORS = {
  bets: 'Unable to load surveillance bets. Please try again.',
  regenerate: 'Unable to generate graph. Please try again.',
} as const

/**
 * Prefer API `message` from thrown Error; otherwise use static fallback.
 * Always returns a fresh Error suitable for toast / react-query.
 */
export function surveillanceCatchError(
  err: unknown,
  staticFallback: string,
): Error {
  if (err instanceof Error) {
    const apiMsg = err.message.trim()
    if (apiMsg) return new Error(apiMsg)
  }
  if (typeof err === 'string' && err.trim()) {
    return new Error(err.trim())
  }
  return new Error(staticFallback)
}

/** Wire shape from `POST /v1/bet/bqGetBetsForSurv`. */
export type CmSurveillanceBetPoint = {
  u?: string
  IP?: string
  t?: string
  p?: number
  s?: number
  d?: number
  h?: string
  ap?: number
  as?: number
}

export type CmSurveillanceHorse = {
  n?: string
  i?: string
  b?: CmSurveillanceBetPoint[]
  u?: string
  fu?: string
}

export type CmSurveillanceGraphPayload = {
  m?: {
    c?: string
    e?: string
    m?: string
    mst?: string | null
    wn?: string
  }
  h?: CmSurveillanceHorse[]
}

export type CmGetBetsForSurveillianceResponse = {
  status?: number
  message?: string
  value?: boolean
  data?: CmSurveillanceGraphPayload | [] | null
}

function mapBetSide(raw: string | undefined): 'BACK' | 'LAY' | null {
  const t = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (t === 'BACK' || t === 'LAY') return t
  return null
}

function mapBetPoint(row: CmSurveillanceBetPoint): SurveillanceBet | null {
  const side = mapBetSide(row.t)
  if (!side) return null
  const p = Number(row.p)
  const s = Number(row.s)
  const d = Number(row.d)
  if (!Number.isFinite(p) || !Number.isFinite(s) || !Number.isFinite(d)) return null
  return {
    u: String(row.u ?? '').trim(),
    IP: row.IP?.trim() || undefined,
    t: side,
    p,
    s,
    d,
    h: row.h?.trim() || undefined,
  }
}

/**
 * Map getBets / bqGetBetsForSurv envelope → graph model.
 * Lab/Agent return `data: []` when filters incomplete or no match payload.
 */
export function mapGetBetsForSurveilliance(
  res: CmGetBetsForSurveillianceResponse,
):
  | { ok: true; data: SurveillanceGraph }
  | { ok: false; message: string } {
  if (res.value === false) {
    return { ok: false, message: res.message?.trim() || 'Unable to load graph' }
  }
  if (typeof res.status === 'number' && (res.status < 200 || res.status >= 300)) {
    return { ok: false, message: res.message?.trim() || `Request failed (${res.status})` }
  }
  const payload = res.data
  if (!payload || Array.isArray(payload)) {
    return {
      ok: false,
      message: res.message?.trim() || 'No surveillance data for this selection',
    }
  }
  const m = payload.m ?? {}
  const horses = Array.isArray(payload.h) ? payload.h : []
  return {
    ok: true,
    data: {
      m: {
        c: String(m.c ?? ''),
        e: String(m.e ?? ''),
        m: String(m.m ?? ''),
        mst: m.mst?.trim() ? m.mst : '',
        wn: String(m.wn ?? ''),
      },
      h: horses.map((horse) => ({
        n: String(horse.n ?? '').trim() || String(horse.i ?? ''),
        i: String(horse.i ?? '').trim(),
        b: (horse.b ?? [])
          .map(mapBetPoint)
          .filter((b): b is SurveillanceBet => Boolean(b)),
        u: String(horse.u ?? '').trim(),
        fu: String(horse.fu ?? '').trim(),
      })),
    },
  }
}
